<!--
  If you need to extend a dialog, remember to place the sub components in the right spot, and also
  the CSS classes such as dialog--text and dialog--header. When extending Vue components, only the
  javascript properties and methods are "merged", not the template. The CSS however does apply to
  the parent also.

  This is an important best practice to ensure a common look across the application.

  NB: showDialog property should always be two-way bound with .sync modifier. Otherwise Vue will throw
  errors about mutating a Prop() directly.

  Note: the #default and #body slots are mutually exclusive, use one or the other.
-->
<script setup lang="ts">
import { watch, computed, useSlots, type PropType, onMounted, onBeforeUnmount } from 'vue';
import DialogActions from './DialogActions.vue';
import MIcon from '@mimir/web/compat/components/m-icon/MIcon.vue';
import type { IconName } from '@mimir/web/compat/components/m-icon/MIcon.types';
import type { SafeHtmlBuilder } from '@mimir/common/util/safeHtmlBuilder';

/*VUE2*/ import { VCard, VCardActions, VCardText, VCol, VDialog, VDivider, VProgressCircular, VRow } from 'vuetify/lib';
import { getI18n } from '@mimir/common/i18n/initializeI18n';
import MAlert from '@mimir/web/compat/components/MAlert.vue';
import MSubheader from '@mimir/web/compat/components/MSubheader.vue';

const props = defineProps({
  showDialog: { type: Boolean, default: false },
  subHeader: { type: String, default: '' },
  icon: { type: String as PropType<IconName>, default: '' },
  bootstrapping: { type: Boolean, default: false },
  header: { type: String, default: 'Header' },
  headerHtml: { type: Object as PropType<SafeHtmlBuilder | null>, default: null },
  maxWidth: { type: Number, default: 300 },
  cancelText: { type: String, default: () => getI18n().t('BaseDialog.Cancel') },
  actionText: { type: String, default: () => getI18n().t('BaseDialog.Ok') },
  disableAction: { type: Boolean, default: false },
  disableCancel: { type: Boolean, default: false },
  loading: { type: Boolean, default: false },
  loadingSecondary: { type: Boolean, default: false },
  infoOnly: { type: Boolean, default: false },
  closeOnActionClick: { type: Boolean, default: false },
  centeredContent: { type: Boolean, default: true },
  closeOnEscape: { type: Boolean, default: true },
  disabledTooltip: { type: String, default: ''},
  disabledCancelTooltip: { type: String, default: ''},
  closeOnOutsideClick: { type: Boolean, default: false },
  baseText: { type: String, default: '' },
  baseHtml: { type: Object as PropType<SafeHtmlBuilder | null>, default: null },
  disableSecondaryAction: { type: Boolean, default: false },
  secondaryActionText: { type: String, default: ''},
  verticalHeightPct: { type: Number, default: 70 },
  alert: { type: String, default: '' },
  hideActionButton: { type: Boolean, default: false },
  hideCancelButton: { type: Boolean, default: false },
  actionButtonIcon: { type: String, default: '' },
  shouldFocusActionButton: { type: Boolean, default: false },
  triggerActionOnMouseDownInsteadOfClick: { type: Boolean, default: false },
});

const emit = defineEmits(['on-close', 'on-show', 'cancelClicked', 'update:showDialog', 'actionClicked', 'secondaryActionClicked']);
const slots = useSlots();

const shouldShow = computed({
  get: () => props.showDialog,
  set: (newValue: boolean) => emit('update:showDialog', newValue)
});

watch(shouldShow, newVal => emit(newVal ? 'on-show' : 'on-close'));

function doAction(emitString: 'actionClicked' | 'secondaryActionClicked', ev?: MouseEvent) {
  emit(emitString, ev);
  if (props.infoOnly) {
    doCancel();
  } else if (props.closeOnActionClick) {
    shouldShow.value = false;
  }
}

function doCancel() {
  emit('cancelClicked');
  shouldShow.value = false;
}

function onEscape() {
  if (props.closeOnEscape) {
    doCancel();
  }
}

function onOutsideClicked() {
  if (props.closeOnOutsideClick) {
    shouldShow.value = false;
  }
}

function keydown(event: KeyboardEvent) {
  if (shouldShow.value === true) {
    event.stopPropagation();
  }
}

onMounted(() => {
  document.addEventListener('keydown', keydown);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', keydown);
});

</script>

<template>
  <VDialog
    v-model="shouldShow"
    :overlay-opacity=".9"
    v-bind="$attrs"
    persistent
    :max-width="maxWidth"
    v-on="$listeners"
    @keydown.esc="onEscape"
    @click:outside="onOutsideClicked"
  >
    <VCard
      class="mx-auto"
      :max-width="maxWidth"
      role="dialog"
      aria-label="Base Dialog"
    >
      <VRow
        dense
        align="center"
        justify="start"
        class="flex-nowrap"
      >
        <VCol
          v-if="icon"
          cols="auto"
        >
          <MIcon :icon="icon" size="small" />
        </VCol>
        <VCol>
          <slot name="header" />
          <span
            v-if="!slots.header && Boolean(headerHtml)"
            v-safe-html="headerHtml"
            style="font-size: 12px; font-family: Roboto, Bold"
          />
          <span
            v-else-if="!slots.header"
            style="font-size: 12px; font-family: Roboto, Bold"
            v-text="header"
          />
        </VCol>
      </VRow>
      <VDivider />
      <div
        class="dialog--wrapper"
        :style="`max-height: ${verticalHeightPct}vh; min-height: 80px`"
      >
        <MSubheader
          v-if="subHeader"
          class="dialog--subHeader ma-0 pa-0 py-2"
          style="height: auto; white-space: pre;"
          v-text="subHeader"
        />
        <VRow v-if="alert">
          <VCol>
            <MAlert type="error">
              {{ alert }}
            </MAlert>
          </VCol>
        </VRow>
        <template v-if="bootstrapping">
          <VRow
            align="center"
            justify="center"
          >
            <VCol cols="auto">
              <VProgressCircular indeterminate />
            </VCol>
          </VRow>
        </template>
        <template v-else>
          <slot name="body" />
          <VCardText
            v-if="slots.default"
            :class="[centeredContent && 'dialog--text']"
          >
            <slot>
              Include any text or HTML here.<br><br>
              Tip: use &lt;div v-html="htmlString"&gt;&lt;/div&gt; to set raw HTML here.
              Remember to always escape contents of v-html!<br>
              Otherwise this slot is default, so just including anything like this:<br>
              &lt;BaseDialog&gt; anything here &lt;/BaseDialog&gt; will work.
            </slot>
          </VCardText>
          <VCardText
            v-if="baseText.length > 0"
            :class="[centeredContent && 'dialog--text', header ? 'pt-1' : 'pt-5']"
          >
            {{ baseText }}
          </VCardText>
          <VCardText
            v-else-if="Boolean(baseHtml)"
            :class="[centeredContent && 'dialog--text', header ? 'pt-1' : 'pt-5']"
          >
            <div v-safe-html="baseHtml" />
          </VCardText>
        </template>
      </div>
      <slot name="pre-action" />
      <VCardActions class="mt-1 pa-0">
        <VRow
          no-gutters
          justify="end"
        >
          <VCol cols="auto">
            <VRow
              dense
              align="center"
              class="ma-0 pt-1"
            >
              <VCol
                cols="auto"
                class="pa-0"
              >
                <slot name="actions" />
              </VCol>
              <VCol
                cols="auto"
                class="pa-0"
              >
                <DialogActions
                  :actionButtonIcon="actionButtonIcon"
                  :infoOnly="infoOnly"
                  :actionText="actionText"
                  :cancelText="cancelText"
                  :secondaryActionText="secondaryActionText"
                  :disableAction="bootstrapping || disableAction"
                  :disableCancel="disableCancel"
                  :disableSecondaryAction="disableSecondaryAction"
                  :loading="loading"
                  :loadingSecondary="loadingSecondary"
                  :disabledTooltip="disabledTooltip"
                  :disabledCancelTooltip="disabledCancelTooltip"
                  :hideActionButton="hideActionButton"
                  :hideCancelButton="hideCancelButton"
                  :shouldFocusActionButton="shouldFocusActionButton"
                  :triggerActionOnMouseDownInsteadOfClick="triggerActionOnMouseDownInsteadOfClick"
                  @cancelClicked="doCancel"
                  @actionClicked="doAction('actionClicked', $event)"
                  @secondaryActionClicked="doAction('secondaryActionClicked', $event)"
                />
              </VCol>
              <VCol
                cols="auto"
                class="pa-0"
              >
                <slot name="actions-suffix" />
              </VCol>
            </VRow>
          </VCol>
        </VRow>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<style lang="stylus" scoped>
.dialog
  &--header
    font-size: 16px !important
    padding-top: 4px !important
    padding-bottom: 4px !important
  &--subHeader
    font-size: 12px
    font-weight: normal
  &--text
    font-size: 12px
    text-align: left
    color: var(--v-default-base)
  &--wrapper
    overflow-y: auto
    overflow-x: hidden
    padding : 4px 2px
</style>
